import {useThemeColors} from '@nfq/colors';

/**
 * Represents the progress and error state of file uploads.
 * This interface is used to track the progress and any errors associated with multiple file uploads.
 */
export interface FileProgress {
    /**
     * An array where each index corresponds to a file, containing an error message if present,
     * or `null`/`undefined` if no error exists.
     */
    fileErrors: (string | null | undefined)[];
    /**
     * An array where each index corresponds to a file, containing the upload progress
     * as a number (percentage) or `'done'` when completed.
     */
    fileProgressBars: (number | 'done' | undefined)[];
}

/**
 * A custom hook for managing asset items in an upload list.
 * This hook retrieves the upload progress and error state for a specific asset
 * based on its index in the `progressData` object. It also provides a `handleDelete`
 * function to remove the asset from the list.
 *
 * @param index        The index of the asset in the upload list.
 * @param onDelete     A callback function triggered when the asset is deleted.
 * @param onDownload   A callback function triggered when the download action is performed.
 * @param progressData An object containing progress and error information for file uploads.
 * @returns An object containing:
 * - `colors`: The theme colors from `useThemeColors`.
 * - `error`: Any error message related to the asset upload.
 * - `handleDelete`: A function to delete the asset from the list.
 * - `progress`: The upload progress value of the asset.
 *
 * @example
 * ```tsx
 * const { colors, error, handleDelete, progress } = useFileItem(0, "assetField", progressData, "file123", handleDelete);
 * // Logs the progress value for the first asset.
 * ```
 */
export const useFileItem = (
    index: number,
    onDelete?: (index: number) => void,
    onDownload?: (index: number) => void,
    progressData?: FileProgress
) => {
    const colors = useThemeColors();

    const progress = progressData ? progressData.fileProgressBars[index] : 'done';

    const error = progressData ? progressData.fileErrors[index] : null;
    /**
     * Handles the deletion of an asset item.
     * Calls the `onDelete` callback with the asset's index to remove it from the list.
     */
    const handleDelete = () => {
        if (onDelete) onDelete(index);
    };

    /**
     * Handles the download action for an asset item.
     * Calls the `onDownload` callback to trigger the download process.
     */
    const handleDownload = () => {
        if (onDownload) onDownload(index);
    };

    const showDownloadButton = progress === 'done' && !error && onDownload !== undefined;
    const showDeleteButton = (progress === 'done' || typeof error === 'string')
        && onDelete !== undefined;

    return {
        colors,
        error,
        handleDelete,
        handleDownload,
        progress,
        showDeleteButton,
        showDownloadButton
    };
};